"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Link } from "@/components/link";
import { NotificationItemContent } from "@/components/notifications/notification-item-content";
import { cn } from "@/lib/utils";
import { getNotificationsInfiniteQueryOptions } from "@/routes/_app/notifications/notifications-query";
import { client } from "@/utils/orpc.client";
import type { ClientNotificationItem } from "@garimpa/api/notifications/toClientNotification";

import { getNotificationPreviewQueryOptions } from "./notification-preview-query";
import { decrementUnreadNotificationsCount } from "./unread-count-query";

interface NotificationItemProps {
  notification: ClientNotificationItem;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const queryClient = useQueryClient();

  const markReadMutation = useMutation({
    mutationFn: () => client.notifications.markRead({ id: notification.id }),
    onSuccess: () => {
      if (!notification.read) {
        decrementUnreadNotificationsCount(queryClient);
      }
      queryClient.invalidateQueries({
        queryKey: getNotificationPreviewQueryOptions().queryKey,
      });
      queryClient.invalidateQueries({ queryKey: getNotificationsInfiniteQueryOptions().queryKey });
    },
  });

  const handleActivate = () => {
    if (!notification.read && !markReadMutation.isPending) {
      markReadMutation.mutate();
    }
  };

  const rowClass = cn(
    "w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors hover:bg-secondary/50",
    !notification.read && "bg-primary/5",
  );

  if (notification.primaryHref) {
    return (
      <Link to={notification.primaryHref} className={rowClass} onClick={handleActivate}>
        <NotificationItemContent notification={notification} />
      </Link>
    );
  }

  return (
    <button type="button" onClick={handleActivate} className={rowClass}>
      <NotificationItemContent notification={notification} />
    </button>
  );
}
